// packages/shared-components/src/components/Watermark/Watermark.tsx

import { useEffect, useMemo, useState } from "react";
import { WatermarkFont, WatermarkProps } from "./types";
import "./Watermark.css";

const DEFAULT_GAP_X = 100;
const DEFAULT_GAP_Y = 100;
const FONT_GAP = 3;

type MarkTile = {
  url: string;
  width: number;
  height: number;
};

function contentLines(content?: string | string[]) {
  if (!content) return [];
  return (Array.isArray(content) ? content : [content]).filter(
    (line) => line.trim().length > 0
  );
}

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("watermark image load failed"));
    image.src = src;
  });
}

function renderTextTile(
  lines: string[],
  options: {
    rotate: number;
    width?: number;
    height?: number;
    gapX: number;
    gapY: number;
    font: Required<
      Pick<
        WatermarkFont,
        "color" | "fontSize" | "fontFamily" | "fontStyle" | "textAlign"
      >
    > &
      Pick<WatermarkFont, "fontWeight">;
  }
): MarkTile | null {
  const measureCanvas = document.createElement("canvas");
  const measureCtx = measureCanvas.getContext("2d");
  if (!measureCtx || !lines.length) return null;

  const fontStyle =
    options.font.fontStyle === "none" ? "normal" : options.font.fontStyle;
  const fontWeight = options.font.fontWeight ?? "normal";
  measureCtx.font = `${fontStyle} ${fontWeight} ${options.font.fontSize}px ${options.font.fontFamily}`;

  const measured = lines.map((text) => {
    const metrics = measureCtx.measureText(text);
    const height = Math.ceil(
      (metrics.actualBoundingBoxAscent || options.font.fontSize * 0.8) +
        (metrics.actualBoundingBoxDescent || options.font.fontSize * 0.2)
    );
    return {
      text,
      width: metrics.width,
      height: height || options.font.fontSize,
    };
  });
  const textWidth = Math.max(...measured.map((line) => line.width));
  const textHeight =
    measured.reduce((sum, line) => sum + line.height, 0) +
    FONT_GAP * Math.max(measured.length - 1, 0);
  const angle = (options.rotate * Math.PI) / 180;
  const boundWidth = Math.ceil(
    Math.abs(Math.sin(angle) * textHeight) +
      Math.abs(Math.cos(angle) * textWidth)
  );
  const boundHeight = Math.ceil(
    Math.abs(Math.sin(angle) * textWidth) +
      Math.abs(Math.cos(angle) * textHeight)
  );
  const markWidth = options.width ?? boundWidth;
  const markHeight = options.height ?? boundHeight;
  const tileWidth = markWidth + options.gapX;
  const tileHeight = markHeight + options.gapY;
  const ratio = window.devicePixelRatio || 1;
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(tileWidth * ratio);
  canvas.height = Math.ceil(tileHeight * ratio);
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(angle);
  ctx.fillStyle = options.font.color;
  ctx.font = `${fontStyle} ${fontWeight} ${options.font.fontSize * ratio}px ${options.font.fontFamily}`;
  ctx.textAlign = options.font.textAlign;
  ctx.textBaseline = "middle";

  const scaledHeight = textHeight * ratio;
  let top = -scaledHeight / 2;
  measured.forEach((line) => {
    const lineHeight = line.height * ratio;
    ctx.fillText(line.text, 0, top + lineHeight / 2);
    top += lineHeight + FONT_GAP * ratio;
  });

  return { url: canvas.toDataURL(), width: tileWidth, height: tileHeight };
}

async function renderImageTile(
  src: string,
  options: {
    rotate: number;
    width?: number;
    height?: number;
    gapX: number;
    gapY: number;
  }
): Promise<MarkTile | null> {
  const image = await loadImage(src);
  const markWidth = options.width ?? image.width;
  const markHeight = options.height ?? image.height;
  const tileWidth = markWidth + options.gapX;
  const tileHeight = markHeight + options.gapY;
  const ratio = window.devicePixelRatio || 1;
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(tileWidth * ratio);
  canvas.height = Math.ceil(tileHeight * ratio);
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((options.rotate * Math.PI) / 180);
  ctx.drawImage(
    image,
    (-markWidth * ratio) / 2,
    (-markHeight * ratio) / 2,
    markWidth * ratio,
    markHeight * ratio
  );
  return { url: canvas.toDataURL(), width: tileWidth, height: tileHeight };
}

/**
 * 页面水印。文字或图片铺成重复背景，不拦截点击。
 * 全屏覆盖时给 style 传 position: fixed 与 inset: 0，并带上 pointerEvents: "none"。
 */
export const Watermark = ({
  zIndex = 9,
  rotate = -22,
  width,
  height,
  image,
  content,
  font,
  gap = [DEFAULT_GAP_X, DEFAULT_GAP_Y],
  offset,
  className,
  style,
  children,
}: WatermarkProps) => {
  const [tile, setTile] = useState<MarkTile | null>(null);
  const lines = useMemo(() => contentLines(content), [content]);
  const gapX = gap[0] ?? DEFAULT_GAP_X;
  const gapY = gap[1] ?? DEFAULT_GAP_Y;
  const offsetX = offset?.[0] ?? gapX / 2;
  const offsetY = offset?.[1] ?? gapY / 2;
  const fontColor = font?.color ?? "rgba(0, 0, 0, 0.15)";
  const fontSize = font?.fontSize ?? 16;
  const fontWeight = font?.fontWeight;
  const fontFamily = font?.fontFamily ?? "sans-serif";
  const fontStyle = font?.fontStyle ?? "normal";
  const textAlign = font?.textAlign ?? "center";

  useEffect(() => {
    let cancelled = false;
    const draw = async () => {
      const shared = { rotate, width, height, gapX, gapY };
      const next = image
        ? await renderImageTile(image, shared).catch(() => null)
        : renderTextTile(lines, {
            ...shared,
            font: {
              color: fontColor,
              fontSize,
              fontWeight,
              fontFamily,
              fontStyle,
              textAlign,
            },
          });
      if (!cancelled) setTile(next);
    };
    void draw();
    return () => {
      cancelled = true;
    };
  }, [
    image,
    lines,
    rotate,
    width,
    height,
    gapX,
    gapY,
    fontColor,
    fontSize,
    fontWeight,
    fontFamily,
    fontStyle,
    textAlign,
  ]);

  const rootClassName = className
    ? `shared-watermark ${className}`
    : "shared-watermark";

  return (
    <div className={rootClassName} style={style}>
      {children}
      <div
        className="shared-watermark__layer"
        aria-hidden="true"
        style={{
          zIndex,
          backgroundImage: tile?.url ? `url("${tile.url}")` : undefined,
          backgroundPosition: `${offsetX}px ${offsetY}px`,
          backgroundSize: tile ? `${tile.width}px ${tile.height}px` : undefined,
        }}
      />
    </div>
  );
};
